/**
 * OperationRecord English/Spanish field normalization for POST/PUT /api/records.
 * DB columns are English; legacy UI and Excel paths may still send Spanish keys.
 */
import type { OperationRecord } from '../../types';

type Row = Record<string, unknown>;

function yesNo(val: unknown): boolean {
  const s = String(val ?? '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return s === 'si' || s === 'sí' || s === 'true' || s === '1' || s === 'liquidada' || val === true || val === 1;
}

/** [canonical English, legacy Spanish] — single source of truth for record EN/ES field pairs. */
export const RECORD_BILINGUAL_PAIRS: [string, string][] = [
  ['client', 'cliente'],
  ['trackingNumber', 'guia'],
  ['logisTrackingNumber', 'guiaLogis'],
  ['businessName', 'razonSocial'],
  ['operator', 'operador'],
  ['pieces', 'piezas'],
  ['weightKg', 'pesoKg'],
  ['length', 'largo'],
  ['width', 'ancho'],
  ['height', 'alto'],
  ['dimensions', 'dimensiones'],
  ['volumetricWeight', 'pesoVolumetrico'],
  ['billingWeight', 'pesoFacturacion'],
  ['quotedWeight', 'pesoCotizado'],
  ['quotedTrm', 'trmCotizacion'],
  ['freightCost', 'costoFlete'],
  ['declaredValue', 'valorConsignado'],
  ['additionalCosts', 'costosAdicionales'],
  ['additionalCostsReason', 'motivoCostosAdicionales'],
  ['discountPercentage', 'descuentoPorcentaje'],
  ['discountValue', 'descuentoValor'],
  ['saleValue', 'valorVenta'],
  ['totalToBill', 'totalAFacturar'],
  ['profitValue', 'valorUtilidad'],
  ['commissionValue', 'valorComision'],
  ['salesRepPercentage', 'porcentajeComercial'],
  ['salesRepCommissionValue', 'valorComisionVendedor'],
  ['netProfit', 'utilidadNeta'],
  ['profitPercentage', 'porcentajeUtilidad'],
  ['logisInvoice', 'facturaLogis'],
  ['carrierInvoice', 'facturaOperador'],
  ['pdfLogisInvoice', 'pdfFacturaLogis'],
  ['pdfCarrierInvoice', 'pdfFacturaOperador'],
  ['pdfInvoice', 'pdfFactura'],
  ['pdfQuote', 'pdfCotizacion'],
  ['pdfPaymentSupport', 'pdfSoportePago'],
  ['pdfLabel', 'pdfGuia'],
  ['commissionPaid', 'comisionPagada'],
  ['clientPaymentStatus', 'estadoCobroCliente'],
  ['clientPaymentMethod', 'metodoPagoCliente'],
  ['clientPaymentDate', 'fechaPagoCliente'],
  ['commissionPaymentDate', 'fechaPagoComision'],
  ['paymentConfirmed', 'pagoConfirmado'],
  ['paidAmount', 'montoPagado'],
  ['pendingBalance', 'saldoPendiente'],
  ['commissionBlockedReason', 'comisionBloqueadaMotivo'],
  ['claimStatus', 'estadoReclamacion'],
  ['uncollectedReason', 'motivoNoRecogida'],
  ['uncollectedReasonDetail', 'motivoNoRecogidaDetalle'],
  ['voidReason', 'motivoAnulacion'],
  ['voidReasonDetail', 'motivoAnulacionDetalle'],
  ['endorsementStatus', 'estado_aval'],
  ['notes', 'observaciones'],
  ['salesRep', 'comercial'],
  ['creditDueDate', 'fechaVencimientoCredito'],
  ['createdAt', 'fechaCreacion'],
  ['isVoided', 'anulada'],
  ['billingCountry', 'pais_facturacion'],
  ['originCity', 'ciudadOrigen'],
  ['destinationCity', 'ciudadDestino'],
  ['originPostalCode', 'codigoPostalOrigen'],
  ['postalCode', 'codigo_postal'],
  ['originQuoteId', 'originCotizacionId'],
  ['originAddress', 'direccionOrigen'],
  ['destinationAddress', 'direccionDestino'],
  ['supplierName', 'proveedorNombre'],
  ['supplierContact', 'proveedorContacto'],
  ['supplierPhone', 'proveedorTelefono'],
  ['operationType', 'tipoOperacion'],
  ['shipmentCutoffDate', 'fechaCorteGuia'],
  ['date', 'fecha'],
  ['previousUpsTrackingNumber', 'guiaAnteriorUPS'],
  ['origin', 'origen'],
  ['creditNoteStatus', 'notaCreditoEstado'],
  ['creditNoteAmountCOP', 'notaCreditoMontoCOP'],
  ['creditNoteNumber', 'notaCreditoNumero'],
  ['creditNoteRequestedAt', 'notaCreditoSolicitadaEn'],
  ['creditNoteReceivedAt', 'notaCreditoRecibidaEn'],
  ['readyToInvoiceNotified', 'listaParaFacturarNotificada'],
  ['carrierPaidAt', 'operadorPagadoEn'],
  ['creditNoteClaimId', 'notaCreditoClaimId'],
  ['commissionClaimId', 'comisionClaimId'],
  ['carrierLabelClaimId', 'guiaCarrierClaimId'],
  ['carrierLabelClaimAt', 'guiaCarrierClaimEn'],
  ['voidClaimId', 'anulacionClaimId'],
];

function pickBilingual<T>(en: T | undefined, es: T | undefined, fallback?: T): T | undefined {
  if (en !== undefined && es !== undefined && en !== es) return es;
  return (en ?? es ?? fallback) as T | undefined;
}

/** Merge edits keeping English and legacy Spanish keys in sync on an operation payload. */
export function normalizeRecordBody(body: Row): Row {
  const r: Row = { ...body };
  const rawVoidEn = body.isVoided;
  const rawVoidEs = body.anulada;
  const voidReasonRaw = body.voidReason ?? body.motivoAnulacion;
  const isVoidAttempt = voidReasonRaw !== undefined && voidReasonRaw !== null && String(voidReasonRaw).trim() !== '';

  // Quote link — frontend sends originQuoteId; legacy paths used originCotizacionId.
  r.originCotizacionId = pickBilingual(
    r.originCotizacionId as string | undefined,
    r.originQuoteId as string | undefined,
  );
  r.originQuoteId = r.originCotizacionId;

  // Commissions.tsx historically sent only Spanish keys while GET returns English.
  // Accounting.tsx spreads stale English + Spanish editFields — Spanish wins when present.
  if (r.comisionPagada !== undefined) r.commissionPaid = yesNo(r.comisionPagada);
  if (r.fechaPagoComision !== undefined) r.commissionPaymentDate = r.fechaPagoComision;
  if (r.estadoCobroCliente !== undefined) r.clientPaymentStatus = r.estadoCobroCliente;
  if (r.pagoConfirmado !== undefined) r.paymentConfirmed = yesNo(r.pagoConfirmado);

  // Accounting.tsx: editFields use Spanish keys but spread includes stale English from GET.
  if (r.facturaLogis !== undefined) r.logisInvoice = r.facturaLogis;
  if (r.facturaOperador !== undefined) r.carrierInvoice = r.facturaOperador;
  if (r.valorConsignado !== undefined) r.declaredValue = r.valorConsignado;
  if (r.estadoReclamacion !== undefined) r.claimStatus = r.estadoReclamacion;
  if (r.observaciones !== undefined) r.notes = r.observaciones;
  if (r.dimensiones !== undefined) r.dimensions = r.dimensiones;
  if (r.metodoPagoCliente !== undefined) r.clientPaymentMethod = r.metodoPagoCliente;
  if (r.fechaPagoCliente !== undefined) r.clientPaymentDate = r.fechaPagoCliente;

  for (const [en, es] of RECORD_BILINGUAL_PAIRS) {
    if (r[en] !== undefined || r[es] !== undefined) {
      const v = pickBilingual(r[en], r[es]);
      if (v !== undefined) {
        r[en] = v;
        r[es] = v;
      }
    }
  }

  // Excel/batch paths store SI/NO strings — coerce after bilingual merge so pickBilingual
  // does not leave commissionPaid as the raw Spanish string when values differ in type.
  if (r.commissionPaid !== undefined || r.comisionPagada !== undefined) {
    const paid = yesNo(r.commissionPaid ?? r.comisionPagada);
    r.commissionPaid = paid;
    r.comisionPagada = paid;
  }
  if (r.paymentConfirmed !== undefined || r.pagoConfirmado !== undefined) {
    const confirmed = yesNo(r.paymentConfirmed ?? r.pagoConfirmado);
    r.paymentConfirmed = confirmed;
    r.pagoConfirmado = confirmed;
  }

  // Spread+patch (GET record + isVoided:1) often leaves stale anulada:0; pickBilingual would
  // prefer Spanish and drop the void. When this payload includes a void reason, honor either flag.
  if (
    isVoidAttempt &&
    rawVoidEn !== undefined &&
    rawVoidEs !== undefined &&
    yesNo(rawVoidEn) !== yesNo(rawVoidEs)
  ) {
    const voided = yesNo(rawVoidEn) || yesNo(rawVoidEs);
    r.isVoided = voided;
    r.anulada = voided;
  } else if (r.isVoided !== undefined || r.anulada !== undefined) {
    const voided = yesNo(r.isVoided ?? r.anulada);
    r.isVoided = voided;
    r.anulada = voided;
  }

  return r;
}

/**
 * The operations form edits English keys, but a record loaded for editing also carries the
 * Spanish mirrors from GET; normalizeRecordBody lets Spanish win when they differ (Accounting
 * sends Spanish edits on purpose), so the form's edits were lost -- city "Cali" saved as
 * "Bogota", an assigned client never stuck (T-6, 2026-10-19; same trap as quotes in T-1).
 * Call it on the form payload before saving: each English value overwrites its mirror.
 */
export function withFormEnglishValues<T extends Row>(record: T): T {
  const out: Row = { ...record };
  for (const [en, es] of RECORD_BILINGUAL_PAIRS) {
    if (out[en] !== undefined) out[es] = out[en];
  }
  return out as T;
}

/**
 * Payload to void an operation from the details modal (T-7). The note written in the modal
 * went only to `notes`, so the stale `observaciones` mirror from GET won and the note was lost;
 * here both keys get it (the void flags are reconciled by normalizeRecordBody).
 */
export function buildVoidedRecord<T extends Row>(
  record: T,
  opts: { note?: string | null; voidReason: string; voidReasonDetail?: string | null },
): T {
  const previous = String(record.notes ?? record.observaciones ?? '');
  const note = String(opts.note ?? '').trim();
  const notes = note ? (previous ? `${previous}\n${note}` : note) : previous;
  return {
    ...record,
    anulada: 1,
    isVoided: 1,
    notes,
    observaciones: notes,
    tracking_status: 'ANULADA',
    voidReason: opts.voidReason || null,
    voidReasonDetail: opts.voidReasonDetail ?? null,
  } as T;
}

/** Body for POST/PUT /api/records — dual keys for transitional clients. */
export function toRecordSaveBody(record: Partial<OperationRecord>): Row {
  return normalizeRecordBody(record as Row);
}
